import { useMemo, useState } from 'react'

import { cn } from '@/lib/utils'
import { SymptomCard } from './symptom-card'
import { PANELS } from './data'
import { SymptomChecks } from '@/entities/signs-symptom'

interface GeneralSectionProps {
  value: SymptomChecks
  onChange: (next: SymptomChecks) => void
}

export function GeneralSection({ value, onChange }: GeneralSectionProps) {
  const [activeKey, setActiveKey] = useState<string>(PANELS[0].key)
  const activePanel = useMemo(() => PANELS.find(p => p.key === activeKey) || PANELS[0], [activeKey])

  const toggle = (id: string) => onChange({ ...value, [id]: !value[id] })

  const panelCount = (key: string) => {
    const p = PANELS.find(p => p.key === key)
    if (!p) return 0
    return p.syms.filter(s => value[s.id]).length
  }

  return (
    <div>
      {/* Divider header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="flex-1 h-px bg-border" />
        <span className="text-[10px] font-bold uppercase tracking-[1.2px] text-muted-foreground whitespace-nowrap">Sinais e sintomas gerais</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <div className="grid grid-cols-1 lgAndUp:grid-cols-[220px_1fr] gap-5">
        {/* Sidenav */}
        <aside className="rounded-2xl border border-border bg-card p-3 shadow-soft self-start lgAndUp:sticky lgAndUp:top-24 max-h-[80vh] overflow-y-auto">
          <div className="text-[10px] font-bold uppercase tracking-[1px] text-muted-foreground px-2 mb-2">Sistemas</div>
          <nav className="flex flex-col gap-1">
            {PANELS.map(p => {
              const isActive = p.key === activeKey
              const cnt = panelCount(p.key)
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setActiveKey(p.key)}
                  className={cn(
                    'flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm text-left transition-colors',
                    isActive
                      ? 'bg-primary/20 text-coentro font-semibold'
                      : 'text-foreground/85 hover:bg-muted hover:text-foreground'
                  )}
                >
                  <span className="text-base leading-none">{p.icon}</span>
                  <span className="flex-1 truncate">{p.title}</span>
                  {cnt > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded bg-primary text-primary-foreground text-[10px] font-bold">{cnt}</span>
                  )}
                </button>
              )
            })}
          </nav>
        </aside>

        {/* Painel */}
        <div className="rounded-2xl border border-border bg-card shadow-soft p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="flex items-center justify-center h-11 w-11 rounded-xl bg-primary/15 text-coentro text-xl">
              {activePanel.icon}
            </div>
            <div>
              <h3 className="font-display text-xl font-semibold text-foreground">{activePanel.title}</h3>
              <p className="text-xs text-muted-foreground mt-0.5">{activePanel.syms.length} sintomas para avaliar</p>
            </div>
          </div>

          <div className="grid grid-cols-1 xsAndUp:grid-cols-2 mdAndUp:grid-cols-3 gap-2">
            {activePanel.syms.map(s => (
              <SymptomCard key={s.id} name={s.name} checked={!!value[s.id]} onToggle={() => toggle(s.id)} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
